import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AuthService } from '../service/authService';
import { useQueryClient } from 'react-query';
import SessionStorageUtils from '../utils/sessionStorageUtils';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const queryClient = useQueryClient();

  const fetchMe = useCallback(async () => {
    try {
      const res = await AuthService.me();
      setUser(res.data);
      return res.data;
    } catch (e) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCsrf = useCallback(async () => {
    try {
      const res = await AuthService.csrf();
      setCsrfToken(res.data.token);
      return res.data.token;
    } catch (e) {
      return null;
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const loginRes = await AuthService.login(email, password);
    // Check if password change is required
    if (loginRes.data?.mustChangePassword) {
      setMustChangePassword(true);
    }
    await fetchMe();
    try {
      const perms = await AuthService.permissions();
      setPermissions(perms.data.sections || {});
    } catch { setPermissions(null); }
    await fetchCsrf();
  }, [fetchMe, fetchCsrf]);

  const logout = useCallback(async () => {
    try { await AuthService.logout(); } catch { /* ignore */ }
    setUser(null);
    setCsrfToken(null);
    setMustChangePassword(false);
    queryClient.clear();
    // Clear session-specific data when user logs out
    SessionStorageUtils.clear();
  }, [queryClient]);

  const onPasswordChanged = useCallback(() => {
    setMustChangePassword(false);
  }, []);

  useEffect(() => { fetchMe().then(() => fetchCsrf()); }, [fetchMe, fetchCsrf]);
  // Fetch permissions when user becomes available (initial load or refresh)
  useEffect(() => {
    if (user?.id) {
      AuthService.permissions()
        .then(p => setPermissions(p.data.sections || {}))
        .catch(() => setPermissions(null));
    } else {
      setPermissions(null);
    }
  }, [user?.id]);

  const value = { user, loading, login, logout, refetchMe: fetchMe, csrfToken, permissions, mustChangePassword, onPasswordChanged };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => useContext(AuthContext);
